/**
 * PATH: packages/email-hub/src/feature/reader-header/menus/useContextMenuRouting.ts
 * FILE_ID: EMAILHUB-READERHEADER-MENUS-ROUTING-001
 * PURPOSE: One owner for chip → lane → generic context-menu routing (D269-02).
 * ROLE IN ARCHITECTURE: Behavior hook mounted once per expanded triage context.
 * MAIN EXPORTS: useContextMenuRouting, ReaderOpenMenu, isContextMenuKey.
 * NON-RESPONSIBILITIES: No menu rendering, no item building, no actions.
 * NOTES FOR FUTURE AI: Precedence — a chip with its OWN menu (CompanyChip,
 *   placement chips) stops propagation itself and never reaches this router;
 *   `data-reader-chip` chips route to a chip scope; bare lane surfaces route
 *   to the lane scope; anything else bubbles to the host's generic
 *   EmailContextMenu. Exactly ONE menu may be open at a time.
 */
import {
  useCallback,
  useMemo,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'

import {
  READER_CHIP_ATTR,
  READER_LANE_ATTR,
  type ReaderChipKind,
  type ReaderContextLaneId,
  type ReaderMenuScope,
} from './menuScopes'

export interface ReaderOpenMenu {
  scope: ReaderMenuScope
  x: number
  y: number
  /** Element to restore focus to when the menu closes (Escape contract). */
  restoreFocusTo: HTMLElement | null
}

export interface LaneSurfaceProps {
  onContextMenu: (event: ReactMouseEvent<HTMLElement>) => void
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void
  [READER_LANE_ATTR]: ReaderContextLaneId
}

export interface ContextMenuRouting {
  /** The single open menu — null when every menu is closed. */
  openMenu: ReaderOpenMenu | null
  closeMenu: () => void
  /** Spread on each lane's root surface. */
  laneSurfaceProps: (lane: ReaderContextLaneId) => LaneSurfaceProps
  /** Reserved ellipsis trigger for touch — anchors under the button. */
  openFromEllipsis: (lane: ReaderContextLaneId, trigger: HTMLElement) => void
}

/** Shift+F10 or the dedicated ContextMenu key (shortcut policy, plan §menus). */
export function isContextMenuKey(event: {
  key: string
  shiftKey: boolean
}): boolean {
  return (event.key === 'F10' && event.shiftKey) || event.key === 'ContextMenu'
}

function resolveScope(
  lane: ReaderContextLaneId,
  target: EventTarget | null,
): ReaderMenuScope {
  if (target instanceof Element) {
    const chipEl = target.closest(`[${READER_CHIP_ATTR}]`)
    if (chipEl) {
      const chip = chipEl.getAttribute(READER_CHIP_ATTR) as ReaderChipKind
      return { kind: 'chip', lane, chip }
    }
  }
  return { kind: 'lane', lane }
}

/**
 * Routing owner for every non-native context menu inside the expanded
 * triage context.
 *
 * Inputs: none (state-only hook). Output: routing state and surface props.
 * Side effects: none beyond React state; callers render LaneContextMenu
 * from `openMenu`. Failure mode: unknown chip kinds fall back to the lane
 * scope inside the item builder, so the user never gets a dead surface.
 */
export function useContextMenuRouting(): ContextMenuRouting {
  const [openMenu, setOpenMenu] = useState<ReaderOpenMenu | null>(null)

  const closeMenu = useCallback(() => setOpenMenu(null), [])

  const laneSurfaceProps = useCallback(
    (lane: ReaderContextLaneId): LaneSurfaceProps => ({
      [READER_LANE_ATTR]: lane,
      onContextMenu: (event) => {
        // Chips with a native menu stopPropagation before this handler; every
        // event that reaches here is lane-owned. Stop the generic reader menu.
        event.preventDefault()
        event.stopPropagation()
        const restore =
          event.target instanceof HTMLElement ? event.target : null
        // Opening replaces any previous menu — exactly one open at a time.
        setOpenMenu({
          scope: resolveScope(lane, event.target),
          x: event.clientX,
          y: event.clientY,
          restoreFocusTo: restore,
        })
      },
      onKeyDown: (event) => {
        if (!isContextMenuKey(event)) return
        event.preventDefault()
        event.stopPropagation()
        const targetEl =
          event.target instanceof HTMLElement ? event.target : null
        const rect = targetEl?.getBoundingClientRect()
        setOpenMenu({
          scope: resolveScope(lane, event.target),
          x: rect ? rect.left : 0,
          y: rect ? rect.bottom : 0,
          restoreFocusTo: targetEl,
        })
      },
    }),
    [],
  )

  const openFromEllipsis = useCallback(
    (lane: ReaderContextLaneId, trigger: HTMLElement) => {
      const rect = trigger.getBoundingClientRect()
      setOpenMenu({
        scope: { kind: 'lane', lane },
        x: rect.left,
        y: rect.bottom,
        restoreFocusTo: trigger,
      })
    },
    [],
  )

  return useMemo(
    () => ({ openMenu, closeMenu, laneSurfaceProps, openFromEllipsis }),
    [openMenu, closeMenu, laneSurfaceProps, openFromEllipsis],
  )
}
